import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { Flame, Menu as MenuIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Toaster } from '@/components/ui/sonner';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const publicLinks = [
  { to: '/', label: 'Home' },
  { to: '/map', label: 'Map' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/forecast', label: 'Forecast' },
  { to: '/info', label: 'Info' },
];

export function PublicShell() {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container flex h-14 items-center gap-3">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
                <MenuIcon className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Flame className="h-5 w-5 text-primary" />
                  Wildfire Reporting
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-4 space-y-1">
                {publicLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.to === '/'}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'block rounded-md px-3 py-2 text-sm hover:bg-muted',
                        isActive && 'bg-primary/10 font-medium text-primary',
                      )
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
                <div className="pt-4">
                  <Button asChild className="w-full">
                    <Link
                      to={isAuthenticated ? '/dashboard' : '/auth/login'}
                      onClick={() => setOpen(false)}
                    >
                      {isAuthenticated ? 'Open dashboard' : 'Sign in'}
                    </Link>
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-primary" />
            <span className="font-semibold tracking-tight">Wildfire Reporting</span>
          </Link>

          <nav className="ml-6 hidden items-center gap-1 md:flex">
            {publicLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-2 text-sm transition',
                    isActive ? 'text-primary font-medium' : 'text-foreground/70 hover:text-foreground',
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto">
            <Button asChild size="sm">
              <Link to={isAuthenticated ? '/dashboard' : '/auth/login'}>
                {isAuthenticated ? '← Dashboard' : 'Sign in'}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* min-w-0 + overflow-x-hidden prevent wide children (forecast day
          tabs, embedded tables) from pushing the page wider than the
          viewport on mobile. */}
      <main className="min-w-0 flex-1 overflow-x-hidden">
        <Outlet />
      </main>

      <footer className="border-t border-border bg-muted/30">
        <div className="container flex flex-col items-center justify-between gap-3 py-6 text-sm text-muted-foreground sm:flex-row">
          <div>© {new Date().getFullYear()} Wildfire Reporting System</div>
          <Link to={isAuthenticated ? '/wildfires/new' : '/auth/login'} className="hover:text-foreground">
            Report a fire →
          </Link>
        </div>
      </footer>
      <Toaster />
    </div>
  );
}

export default PublicShell;
